"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Calculator, CalendarCheck, UserCheck, ArrowRight, Phone, FileCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface BenefitItem {
  id: string;
  title: string;
  description: string;
  iconName: "shield" | "calc" | "calendar" | "user";
  specTag: string;
}
const benefitsData: BenefitItem[] = [{
  id: "tg20",
  title: "TG20:21 operational compliance",
  description: "Full compliance sheets and operational sign-offs provided before erection, ensuring every scaffold structure matches NASC safety criteria.",
  iconName: "shield",
  specTag: "NASC Standard"
}, {
  id: "design",
  title: "Site-specific access planning",
  description: "Access layouts planned around your site conditions, loading bays and temporary roof requirements",
  iconName: "calc",
  specTag: "Site-Specific Planning"
}, {
  id: "inspections",
  title: "Scheduled 7-day statutory inspections",
  description: "Thorough weekly handover checks, digital inspection records, and prompt scaffold tag updating to keep your site fully audit-ready.",
  iconName: "calendar",
  specTag: "Statutory Register"
}, {
  id: "supervisors",
  title: "Dedicated on-site supervisors",
  description: "Experienced site managers providing direct daily coordination with main contractors, managing toolbox talks and handover schedules.",
  iconName: "user",
  specTag: "Direct Accountability"
}];
const accreditationBadges = [{
  name: "NASC",
  label: "Full Compliance Standards"
}, {
  name: "CHAS",
  label: "Accredited Contractor"
}];
function ContractorsContent() {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const matchedPhoto = posts.find((p: any) => p?.acf?.photo_name === "Commercial Site Proof Image" || p?.customFields?.photo_name === "Commercial Site Proof Image" || p?.title === "Commercial Site Proof Image");
  const photoSrc = matchedPhoto?.acf?.image || matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_construction_scaffolding.png";
  const photoAlt = matchedPhoto?.acf?.alt_text || matchedPhoto?.customFields?.alt_text || "Active commercial construction project with structural access scaffolding";
  const isDynamicImage = photoSrc !== "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_construction_scaffolding.png";
  const getIcon = (type: BenefitItem["iconName"]) => {
    switch (type) {
      case "shield":
        return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
      case "calc":
        return <Calculator className="size-6 text-primary" aria-hidden="true" />;
      case "calendar":
        return <CalendarCheck className="size-6 text-primary" aria-hidden="true" />;
      case "user":
        return <UserCheck className="size-6 text-primary" aria-hidden="true" />;
      default:
        return <FileCheck className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-contractors-choose-bmf" data-nav="light" className="bg-background text-foreground border-b border-border py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* Category Datum & Heading Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-0.5 w-6 bg-primary" aria-hidden="true" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Contractor Capabilities & Standards
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Built to keep your commercial site compliant and on schedule
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            From initial design calculations through to scheduled 7-day inspections, your project is backed by verified safety standards, clear documentation, and experienced supervisors.
          </p>
        </div>

        {/* 2-Column Split: Benefits Grid (Left) & Active Site Media Card (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left Column: Benefits Cards */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {benefitsData.map((benefit, index) => <motion.div key={benefit.id} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card rounded-md p-5 md:p-6 transition-[border-color,box-shadow] duration-200 hover:border-primary hover:shadow-md">
                  <CardContent className="p-0 flex flex-col justify-between h-full space-y-4">
                    <div>
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex size-11 items-center justify-center rounded-md bg-accent">
                          {getIcon(benefit.iconName)}
                        </div>
                        <span className="inline-flex items-center rounded-sm bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground border border-border">
                          {benefit.specTag}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-card-foreground mb-2 leading-snug">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Right Column: Image and Accreditation Panel */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Active Commercial Site Photo Card */}
            <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
              <div className="relative aspect-[16/11] w-full overflow-hidden bg-muted">
                {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={photoSrc} data-wvc-srcset={isDynamicImage ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_construction_scaffolding.png-wvc-srcset"} data-wvc-sizes={isDynamicImage ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_construction_scaffolding.png-wvc-sizes"} alt={photoAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />}
                <div className="absolute inset-0 bg-secondary/15 pointer-events-none" />
                <div className="absolute bottom-3 left-3 right-3 rounded-sm bg-secondary/90 backdrop-blur-xs p-3 text-secondary-foreground border border-border/20">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                    Live Project Execution
                  </p>
                  <p className="text-xs text-secondary-foreground/90 mt-0.5">
                    Multi-level commercial scaffolding with integrated edge protection and secure access points.
                  </p>
                </div>
              </div>

              {/* Accreditations Row */}
              <div className="p-5 border-t border-border bg-card">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  Verified Industry Accreditations
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {accreditationBadges.map((badge, idx) => <div key={badge.name} data-index={idx} className="flex flex-col items-center justify-center p-2.5 rounded-sm bg-background border border-border text-center">
                      <span className="text-sm font-bold text-foreground tracking-tight">
                        {badge.name}
                      </span>
                      <span className="text-[11px] text-muted-foreground leading-tight mt-0.5 line-clamp-1">
                        {badge.label}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Direct Contractor Action Bar */}
            <div className="rounded-md border border-border bg-muted/40 p-5 flex flex-col sm:flex-row items-stretch sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-bold text-foreground">
                  Need scaffolding tender pricing?
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Send your site plans and technical specification.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ContactRequestQuotePage">
                    <span>Request quote</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="border-border text-foreground hover:bg-accent">
                  <a href="tel:0800000000">
                    <Phone className="size-3.5" aria-hidden="true" />
                    <span>Call team</span>
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
export default function Contractors() {
  const wp_query = new WP_Query({
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <ContractorsContent />
    </PostsProvider>;
}